/**
 * SSE (Server-Sent Events) 서비스
 * 백엔드 서버와의 실시간 이벤트 통신을 담당
 */

import type { SSEEvent } from "@/types";

type SSEMessageCallback = (data: SSEEvent) => void;
type SSEErrorCallback = (error: Error) => void;

type SSEEventType =
  | "viewr_creation"
  | "file_conversion"
  | "viewr_update"
  | string;

type SseTransportKind = "eventsource" | "fetch";

export interface SSEConnectOptions {
  owner?: string;
  tokenProvider?: () => Promise<string | null>;
}

export interface SSEConnectionDebugSnapshot {
  eventType: string;
  owner: string | null;
  jobId: string | null;
  openedAt: number;
  reconnectAttempt: number;
  hasReconnectTimer: boolean;
  transportKind: SseTransportKind;
}

interface EventListener {
  onMessage: SSEMessageCallback;
  onError: SSEErrorCallback;
}

interface ConnectionStatus {
  [eventType: string]: boolean;
}

interface ParsedSseFrame {
  event?: string;
  data: string;
}

export const parseSseFrames = (
  buffer: string,
): { frames: ParsedSseFrame[]; remainder: string } => {
  const normalized = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const parts = normalized.split("\n\n");
  const remainder = parts.pop() ?? "";

  const frames = parts
    .map((part) => {
      let eventName: string | undefined;
      const dataLines: string[] = [];

      part.split("\n").forEach((line) => {
        if (!line || line.startsWith(":")) return;

        const separatorIndex = line.indexOf(":");
        const field =
          separatorIndex === -1 ? line : line.slice(0, separatorIndex);
        const rawValue =
          separatorIndex === -1 ? "" : line.slice(separatorIndex + 1);
        const value = rawValue.startsWith(" ")
          ? rawValue.slice(1)
          : rawValue;

        if (field === "event") {
          eventName = value;
        } else if (field === "data") {
          dataLines.push(value);
        }
      });

      if (dataLines.length === 0) return null;

      return {
        ...(eventName ? { event: eventName } : {}),
        data: dataLines.join("\n"),
      };
    })
    .filter((frame): frame is ParsedSseFrame => frame !== null);

  return { frames, remainder };
};

interface TransportErrorOptions {
  reconnect: boolean;
}

interface SseTransportCallbacks {
  onOpen: () => void;
  onMessage: (data: SSEEvent) => void;
  onError: (error: Error, options: TransportErrorOptions) => void;
}

interface SseTransportConnection {
  kind: SseTransportKind;
  open: () => void;
  close: () => void;
  isOpen: () => boolean;
}

const isDevelopment = (): boolean => process.env.NODE_ENV === "development";

const getSseEventSummary = (
  data: SSEEvent,
): {
  eventType: string;
  jobId: string;
  status: string;
} => ({
  eventType: String(data.event || data.type || "unknown"),
  jobId: String(data.job_id || data.jobId || "none"),
  status: String(data.status || "unknown"),
});

class EventSourceTransportConnection implements SseTransportConnection {
  kind: SseTransportKind = "eventsource";
  private eventSource: EventSource | null = null;

  constructor(
    private readonly eventType: SSEEventType,
    private readonly url: string,
    private readonly token: string,
    private readonly callbacks: SseTransportCallbacks,
  ) {}

  open(): void {
    const urlWithToken = `${this.url}?token=${encodeURIComponent(this.token)}`;
    const eventSource = new EventSource(urlWithToken);
    this.eventSource = eventSource;

    eventSource.onopen = () => {
      this.callbacks.onOpen();
    };

    eventSource.onmessage = (event: MessageEvent) => {
      this.handleMessage(event, "default");
    };

    eventSource.addEventListener(this.eventType, (event: MessageEvent) => {
      this.handleMessage(event, this.eventType);
    });

    eventSource.addEventListener("heartbeat", () => {
      if (isDevelopment()) {
        // Heartbeats are intentionally quiet by default.
      }
    });

    eventSource.addEventListener("open", () => {
      if (isDevelopment()) {
        console.log("📨 SSE 연결 열림:", { eventType: this.eventType });
      }
    });

    eventSource.addEventListener("error", () => {
      if (isDevelopment()) {
        console.log("📨 SSE 에러 이벤트:", { eventType: this.eventType });
      }
    });

    eventSource.addEventListener("message", (event: MessageEvent) => {
      if (!isDevelopment()) return;
      try {
        const data = JSON.parse(event.data) as SSEEvent;
        console.log("📨 [DEBUG] 모든 SSE 이벤트 수신:", {
          ...getSseEventSummary(data),
        });
      } catch {
        console.log("📨 [DEBUG] SSE 원시 이벤트 파싱 실패:", {
          eventType: this.eventType,
        });
      }
    });

    eventSource.onerror = () => {
      eventSource.close();
      if (this.eventSource === eventSource) {
        this.eventSource = null;
      }

      console.error(`❌ SSE 연결 오류 [${this.eventType}]:`);

      if (eventSource.readyState === EventSource.CLOSED) {
        console.error(
          `🚨 SSE 연결이 닫힘 [${this.eventType}] - 백엔드 서버가 실행되지 않았을 수 있습니다`,
        );
      }

      this.callbacks.onError(
        new Error(`SSE connection error: ${this.eventType}`),
        { reconnect: true },
      );
    };
  }

  close(): void {
    this.eventSource?.close();
    this.eventSource = null;
  }

  isOpen(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  private handleMessage(event: MessageEvent, source: string): void {
    try {
      const data = JSON.parse(event.data) as SSEEvent;
      if (isDevelopment()) {
        console.log(`📨 SSE 메시지 수신 [${source}]:`, getSseEventSummary(data));
      }
      if (data?.type === "heartbeat") return;
      this.callbacks.onMessage(data);
    } catch (error) {
      console.error(`❌ SSE 메시지 파싱 오류 [${source}]:`, error);
    }
  }
}

class FetchStreamTransportConnection implements SseTransportConnection {
  kind: SseTransportKind = "fetch";
  private abortController: AbortController | null = null;
  private opened = false;
  private closed = false;

  constructor(
    private readonly eventType: SSEEventType,
    private readonly url: string,
    private readonly token: string,
    private readonly callbacks: SseTransportCallbacks,
  ) {}

  open(): void {
    void this.run();
  }

  close(): void {
    this.closed = true;
    this.abortController?.abort();
  }

  isOpen(): boolean {
    return this.opened && !this.closed;
  }

  private async run(): Promise<void> {
    const abortController = new AbortController();
    this.abortController = abortController;

    try {
      const response = await fetch(this.url, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${this.token}`,
        },
        signal: abortController.signal,
      });

      if (this.closed) return;

      if (response.status === 401 || response.status === 403) {
        this.callbacks.onError(
          new Error(`SSE authentication failed: ${this.eventType}`),
          { reconnect: false },
        );
        return;
      }

      if (!response.ok) {
        throw new Error(`SSE fetch failed: ${this.eventType} (${response.status})`);
      }

      if (!response.body) {
        throw new Error(`SSE fetch response has no body: ${this.eventType}`);
      }

      this.opened = true;
      this.callbacks.onOpen();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      try {
        while (!this.closed) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSseFrames(buffer);
          buffer = parsed.remainder;

          parsed.frames.forEach((frame) => {
            this.dispatchFrame(frame);
          });
        }
      } finally {
        reader.releaseLock();
      }

      if (!this.closed) {
        throw new Error(`SSE fetch stream closed: ${this.eventType}`);
      }
    } catch (error) {
      if (this.closed || (error as { name?: string })?.name === "AbortError") {
        return;
      }

      this.callbacks.onError(
        error instanceof Error
          ? error
          : new Error(`SSE fetch stream failed: ${this.eventType}`),
        { reconnect: true },
      );
    }
  }

  private dispatchFrame(frame: ParsedSseFrame): void {
    const data = JSON.parse(frame.data) as SSEEvent;
    if (isDevelopment()) {
      console.log("📨 SSE fetch stream message:", {
        ...getSseEventSummary(data),
        streamEvent: frame.event ?? null,
      });
    }
    if (data?.type === "heartbeat") return;
    this.callbacks.onMessage(data);
  }
}

class SSEService {
  private connections: Map<string, SseTransportConnection>;
  private eventListeners: Map<string, EventListener>;
  private reconnectAttempts: Map<string, number>;
  private reconnectTimers: Map<string, ReturnType<typeof setTimeout>>;
  private jobIds: Map<string, string>;
  private connectionMetadata: Map<
    string,
    Pick<SSEConnectionDebugSnapshot, "owner" | "openedAt" | "transportKind">
  >;
  private openWaiters: Map<
    string,
    { promise: Promise<void>; resolve: () => void; reject: (error: Error) => void }
  >;
  private maxReconnectAttempts: number;
  private reconnectDelay: number;

  constructor() {
    this.connections = new Map();
    this.eventListeners = new Map();
    this.reconnectAttempts = new Map();
    this.reconnectTimers = new Map();
    this.jobIds = new Map();
    this.connectionMetadata = new Map();
    this.openWaiters = new Map();
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }

  private isDevelopment(): boolean {
    return isDevelopment();
  }

  private assertConnectAllowed(
    eventType: SSEEventType,
    options?: SSEConnectOptions,
  ): void {
    if (
      eventType === "viewr_creation" &&
      options?.owner !== "viewRCreationSseChannel"
    ) {
      throw new Error(
        "viewr_creation SSE must be opened through viewRCreationSseChannel.",
      );
    }
  }

  private getTransportKind(): SseTransportKind {
    const configured = process.env.REACT_APP_SSE_TRANSPORT;
    if (!configured || configured === "eventsource") return "eventsource";
    if (configured === "fetch") return "fetch";

    if (this.isDevelopment()) {
      console.warn(
        `[SSE] Unknown REACT_APP_SSE_TRANSPORT "${configured}". Falling back to eventsource.`,
      );
    }
    return "eventsource";
  }

  private clearReconnectTimer(eventType: SSEEventType): void {
    const timer = this.reconnectTimers.get(eventType);
    if (timer) {
      clearTimeout(timer);
      this.reconnectTimers.delete(eventType);
    }
  }

  private getOrCreateOpenWaiter(eventType: SSEEventType): {
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
  } {
    const existing = this.openWaiters.get(eventType);
    if (existing) return existing;

    let resolve!: () => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    const waiter = { promise, resolve, reject };
    this.openWaiters.set(eventType, waiter);
    return waiter;
  }

  private handleOpen(eventType: SSEEventType): void {
    if (this.isDevelopment()) {
      console.log(`✅ SSE 연결 성공: ${eventType}`, {
        owner: this.connectionMetadata.get(eventType)?.owner ?? null,
        transportKind: this.connectionMetadata.get(eventType)?.transportKind,
      });
    }

    this.reconnectAttempts.set(eventType, 0);
    const waiter = this.openWaiters.get(eventType);
    if (waiter) {
      waiter.resolve();
      this.openWaiters.delete(eventType);
    }
  }

  private handleTransportError({
    eventType,
    connection,
    token,
    onMessage,
    onError,
    jobId,
    options,
    reconnect,
    error,
  }: {
    eventType: SSEEventType;
    connection: SseTransportConnection;
    token: string;
    onMessage: SSEMessageCallback;
    onError: SSEErrorCallback;
    jobId: string | null;
    options?: SSEConnectOptions;
    reconnect: boolean;
    error: Error;
  }): void {
    const waiter = this.openWaiters.get(eventType);
    if (waiter) {
      waiter.reject(error);
      this.openWaiters.delete(eventType);
    }

    if (!reconnect) {
      this.disconnect(eventType);
      onError(error);
      return;
    }

    const attempts = this.reconnectAttempts.get(eventType) || 0;

    if (attempts >= this.maxReconnectAttempts) {
      console.error(`🚨 SSE 최대 재연결 시도 초과 [${eventType}]`);
      this.disconnect(eventType);
      onError(
        new Error(
          `SSE connection failed: ${eventType}. Check the backend server status.`,
        ),
      );
      return;
    }

    const nextAttempt = attempts + 1;
    this.reconnectAttempts.set(eventType, nextAttempt);

    if (this.isDevelopment()) {
      console.log(
        `🔄 SSE 재연결 시도 [${eventType}]: ${nextAttempt}/${this.maxReconnectAttempts}`,
      );
    }

    this.clearReconnectTimer(eventType);
    const reconnectTimer = setTimeout(() => {
      this.reconnectTimers.delete(eventType);
      if (this.connections.get(eventType) !== connection) {
        return;
      }

      void this.reconnect({
        eventType,
        token,
        onMessage,
        onError,
        jobId,
        options,
        nextAttempt,
      });
    }, this.reconnectDelay * nextAttempt);
    this.reconnectTimers.set(eventType, reconnectTimer);
  }

  private async reconnect({
    eventType,
    token,
    onMessage,
    onError,
    jobId,
    options,
    nextAttempt,
  }: {
    eventType: SSEEventType;
    token: string;
    onMessage: SSEMessageCallback;
    onError: SSEErrorCallback;
    jobId: string | null;
    options?: SSEConnectOptions;
    nextAttempt: number;
  }): Promise<void> {
    const resolvedToken = options?.tokenProvider
      ? await options.tokenProvider()
      : token;

    if (!resolvedToken) {
      this.disconnect(eventType);
      onError(new Error("SSE authentication token missing."));
      return;
    }

    this.disconnect(eventType);
    this.reconnectAttempts.set(eventType, nextAttempt);
    this.connect(eventType, resolvedToken, onMessage, onError, jobId, options);
  }

  private createConnection({
    kind,
    eventType,
    url,
    token,
    callbacks,
  }: {
    kind: SseTransportKind;
    eventType: SSEEventType;
    url: string;
    token: string;
    callbacks: SseTransportCallbacks;
  }): SseTransportConnection {
    if (kind === "fetch") {
      return new FetchStreamTransportConnection(eventType, url, token, callbacks);
    }

    return new EventSourceTransportConnection(eventType, url, token, callbacks);
  }

  async waitForOpen(
    eventType: SSEEventType,
    timeoutMs = 2500,
  ): Promise<void> {
    const connection = this.connections.get(eventType);
    if (connection?.isOpen()) {
      return;
    }

    const waiter = this.getOrCreateOpenWaiter(eventType);
    await Promise.race([
      waiter.promise,
      new Promise<void>((_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error(`SSE open timeout: ${eventType} (${timeoutMs}ms)`),
            ),
          timeoutMs,
        ),
      ),
    ]);
  }

  connect(
    eventType: SSEEventType,
    token: string,
    onMessage: SSEMessageCallback,
    onError: SSEErrorCallback,
    jobId: string | null = null,
    options?: SSEConnectOptions,
  ): void {
    this.assertConnectAllowed(eventType, options);

    if (this.connections.has(eventType)) {
      if (this.isDevelopment()) {
        console.log(`⚠️ SSE 연결이 이미 존재합니다: ${eventType}`);
      }
      return;
    }

    this.clearReconnectTimer(eventType);

    const baseUrl =
      process.env.REACT_APP_API_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      "https://viewrapi-876455037250.asia-northeast3.run.app";
    const url = `${baseUrl}/sse/events/${eventType}`;
    const transportKind = this.getTransportKind();

    if (this.isDevelopment()) {
      console.log(`🔗 SSE 연결 시작: ${eventType} - ${url}`, {
        owner: options?.owner ?? null,
        jobId: jobId ?? null,
        transportKind,
      });
    }

    const connection = this.createConnection({
      kind: transportKind,
      eventType,
      url,
      token,
      callbacks: {
        onOpen: () => this.handleOpen(eventType),
        onMessage,
        onError: (error, errorOptions) =>
          this.handleTransportError({
            eventType,
            connection,
            token,
            onMessage,
            onError,
            jobId,
            options,
            reconnect: errorOptions.reconnect,
            error,
          }),
      },
    });

    this.connections.set(eventType, connection);
    this.eventListeners.set(eventType, { onMessage, onError });
    this.connectionMetadata.set(eventType, {
      owner: options?.owner ?? null,
      openedAt: Date.now(),
      transportKind,
    });

    if (jobId) {
      this.jobIds.set(eventType, jobId);
      if (this.isDevelopment()) {
        console.log(`📝 SSE job_id 추적 설정: ${eventType} -> ${jobId}`);
      }
    }

    connection.open();
  }

  setJobId(eventType: SSEEventType, jobId: string): void {
    if (!eventType || !jobId) return;
    this.jobIds.set(eventType, jobId);
    if (this.isDevelopment()) {
      console.log(`📝 SSE job_id 업데이트: ${eventType} -> ${jobId}`);
    }
  }

  disconnect(eventType: SSEEventType): void {
    this.clearReconnectTimer(eventType);
    const connection = this.connections.get(eventType);
    if (connection) {
      if (this.isDevelopment()) {
        console.log(`🔌 SSE 연결 해제: ${eventType}`);
      }
      connection.close();
      this.connections.delete(eventType);
    }
    this.eventListeners.delete(eventType);
    this.reconnectAttempts.delete(eventType);
    this.jobIds.delete(eventType);
    this.connectionMetadata.delete(eventType);
    const waiter = this.openWaiters.get(eventType);
    if (waiter) {
      waiter.reject(new Error(`SSE disconnected before open: ${eventType}`));
      this.openWaiters.delete(eventType);
    }
  }

  disconnectAll(): void {
    if (this.isDevelopment()) {
      console.log("🔌 모든 SSE 연결 해제");
    }
    const eventTypes = Array.from(
      new Set([
        ...Array.from(this.connections.keys()),
        ...Array.from(this.reconnectTimers.keys()),
      ]),
    );
    eventTypes.forEach((eventType) => {
      this.disconnect(eventType);
    });
  }

  isConnected(eventType: SSEEventType): boolean {
    return this.connections.has(eventType);
  }

  getConnectionStatus(): ConnectionStatus {
    const status: ConnectionStatus = {};
    this.connections.forEach((_, eventType) => {
      status[eventType] = true;
    });
    return status;
  }

  getConnectionDebugSnapshot(): SSEConnectionDebugSnapshot[] {
    const eventTypes = Array.from(
      new Set([
        ...Array.from(this.connections.keys()),
        ...Array.from(this.reconnectTimers.keys()),
      ]),
    );

    return eventTypes.map((eventType) => {
      const metadata = this.connectionMetadata.get(eventType);
      return {
        eventType,
        owner: metadata?.owner ?? null,
        jobId: this.jobIds.get(eventType) ?? null,
        openedAt: metadata?.openedAt ?? 0,
        reconnectAttempt: this.reconnectAttempts.get(eventType) ?? 0,
        hasReconnectTimer: this.reconnectTimers.has(eventType),
        transportKind: metadata?.transportKind ?? this.getTransportKind(),
      };
    });
  }

  getCurrentJobId(eventType: SSEEventType): string | null {
    return this.jobIds.get(eventType) || null;
  }

  clearJobId(eventType: SSEEventType): void {
    this.jobIds.delete(eventType);
  }
}

export const sseService = new SSEService();

export const useSSE = () => {
  return {
    connect: sseService.connect.bind(sseService),
    disconnect: sseService.disconnect.bind(sseService),
    disconnectAll: sseService.disconnectAll.bind(sseService),
    isConnected: sseService.isConnected.bind(sseService),
    getConnectionStatus: sseService.getConnectionStatus.bind(sseService),
    getConnectionDebugSnapshot:
      sseService.getConnectionDebugSnapshot.bind(sseService),
  };
};

export default sseService;
